// Generates a Facturae 3.2.2 XML document (the Spanish e-invoice / FACe format).
// NOTE: the file produced here is unsigned. Legal submission to FACe requires an
// XAdES electronic signature with the issuer's digital certificate, which is done
// server-side (see the Verifactu certificate flow). This builds the payload.

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

const n2 = (v) => Number(v || 0).toFixed(2);
const n6 = (v) => Number(v || 0).toFixed(6);

// Group invoice lines by VAT rate to build the TaxesOutputs block.
function taxBreakdown(lines) {
  const map = {};
  for (const l of lines) {
    const base = Number(l.total ?? l.quantity * l.unit_price) || 0;
    const rate = Number(l.tax_rate || 0);
    if (!map[rate]) map[rate] = { base: 0, rate };
    map[rate].base += base;
  }
  return Object.values(map).map(t => ({
    rate: t.rate,
    base: t.base,
    amount: t.base * (t.rate / 100),
  }));
}

export function buildFacturaeXML({ invoice, lines, profile, client }) {
  const taxes = taxBreakdown(lines);
  const totalTax = taxes.reduce((s, t) => s + t.amount, 0);
  const totalGross = lines.reduce((s, l) => s + (Number(l.total ?? l.quantity * l.unit_price) || 0), 0);
  const totalInvoice = totalGross + totalTax;

  const sellerNif = esc(profile?.cif_nif || '');
  const sellerName = esc(profile?.company_name || '');
  const buyerNif = esc(client?.tax_id || '');
  const buyerName = esc(client?.name || 'Consumidor final');

  const taxLines = taxes.map(t => `
        <Tax>
          <TaxTypeCode>01</TaxTypeCode>
          <TaxRate>${n2(t.rate)}</TaxRate>
          <TaxableBase><TotalAmount>${n2(t.base)}</TotalAmount></TaxableBase>
          <TaxAmount><TotalAmount>${n2(t.amount)}</TotalAmount></TaxAmount>
        </Tax>`).join('');

  const itemLines = lines.map(l => {
    const qty = Number(l.quantity || 0);
    const unit = Number(l.unit_price || 0);
    const gross = Number(l.total ?? qty * unit) || 0;
    const rate = Number(l.tax_rate || 0);
    return `
      <InvoiceLine>
        <ItemDescription>${esc(l.description)}</ItemDescription>
        <Quantity>${n6(qty)}</Quantity>
        <UnitOfMeasure>01</UnitOfMeasure>
        <UnitPriceWithoutTax>${n6(unit)}</UnitPriceWithoutTax>
        <TotalCost>${n2(gross)}</TotalCost>
        <GrossAmount>${n2(gross)}</GrossAmount>
        <TaxesOutputs>
          <Tax>
            <TaxTypeCode>01</TaxTypeCode>
            <TaxRate>${n2(rate)}</TaxRate>
            <TaxableBase><TotalAmount>${n2(gross)}</TotalAmount></TaxableBase>
            <TaxAmount><TotalAmount>${n2(gross * rate / 100)}</TotalAmount></TaxAmount>
          </Tax>
        </TaxesOutputs>
      </InvoiceLine>`;
  }).join('');

  const issue = (invoice.issue_date || '').split('T')[0];

  return `<?xml version="1.0" encoding="UTF-8"?>
<fe:Facturae xmlns:fe="http://www.facturae.gob.es/formato/Versiones/Facturaev3_2_2.xml">
  <FileHeader>
    <SchemaVersion>3.2.2</SchemaVersion>
    <Modality>I</Modality>
    <InvoiceIssuerType>EM</InvoiceIssuerType>
    <Batch>
      <BatchIdentifier>${esc(invoice.invoice_number)}</BatchIdentifier>
      <InvoicesCount>1</InvoicesCount>
      <TotalInvoicesAmount><TotalAmount>${n2(totalInvoice)}</TotalAmount></TotalInvoicesAmount>
      <TotalOutstandingAmount><TotalAmount>${n2(totalInvoice)}</TotalAmount></TotalOutstandingAmount>
      <TotalExecutableAmount><TotalAmount>${n2(totalInvoice)}</TotalAmount></TotalExecutableAmount>
      <InvoiceCurrencyCode>EUR</InvoiceCurrencyCode>
    </Batch>
  </FileHeader>
  <Parties>
    <SellerParty>
      <TaxIdentification>
        <PersonTypeCode>J</PersonTypeCode>
        <ResidenceTypeCode>R</ResidenceTypeCode>
        <TaxIdentificationNumber>${sellerNif}</TaxIdentificationNumber>
      </TaxIdentification>
      <LegalEntity>
        <CorporateName>${sellerName}</CorporateName>
        <AddressInSpain>
          <Address>${esc(profile?.address || '')}</Address>
          <PostCode>00000</PostCode>
          <Town>-</Town>
          <Province>-</Province>
          <CountryCode>ESP</CountryCode>
        </AddressInSpain>
      </LegalEntity>
    </SellerParty>
    <BuyerParty>
      <TaxIdentification>
        <PersonTypeCode>F</PersonTypeCode>
        <ResidenceTypeCode>R</ResidenceTypeCode>
        <TaxIdentificationNumber>${buyerNif}</TaxIdentificationNumber>
      </TaxIdentification>
      <LegalEntity>
        <CorporateName>${buyerName}</CorporateName>
        <AddressInSpain>
          <Address>${esc(client?.address || '-')}</Address>
          <PostCode>00000</PostCode>
          <Town>-</Town>
          <Province>-</Province>
          <CountryCode>ESP</CountryCode>
        </AddressInSpain>
      </LegalEntity>
    </BuyerParty>
  </Parties>
  <Invoices>
    <Invoice>
      <InvoiceHeader>
        <InvoiceNumber>${esc(invoice.invoice_number)}</InvoiceNumber>
        <InvoiceDocumentType>FC</InvoiceDocumentType>
        <InvoiceClass>OO</InvoiceClass>
      </InvoiceHeader>
      <InvoiceIssueData>
        <IssueDate>${esc(issue)}</IssueDate>
        <InvoiceCurrencyCode>EUR</InvoiceCurrencyCode>
        <TaxCurrencyCode>EUR</TaxCurrencyCode>
        <LanguageName>es</LanguageName>
      </InvoiceIssueData>
      <TaxesOutputs>${taxLines}
      </TaxesOutputs>
      <InvoiceTotals>
        <TotalGrossAmount>${n2(totalGross)}</TotalGrossAmount>
        <TotalGrossAmountBeforeTaxes>${n2(totalGross)}</TotalGrossAmountBeforeTaxes>
        <TotalTaxOutputs>${n2(totalTax)}</TotalTaxOutputs>
        <TotalTaxesWithheld>0.00</TotalTaxesWithheld>
        <InvoiceTotal>${n2(totalInvoice)}</InvoiceTotal>
        <TotalOutstandingAmount>${n2(totalInvoice)}</TotalOutstandingAmount>
        <TotalExecutableAmount>${n2(totalInvoice)}</TotalExecutableAmount>
      </InvoiceTotals>
      <Items>${itemLines}
      </Items>
    </Invoice>
  </Invoices>
</fe:Facturae>`;
}

export function downloadFacturaeFile(xml, invoiceNumber) {
  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `facturae-${invoiceNumber}.xsig.xml`;
  a.click();
  URL.revokeObjectURL(url);
}
